import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Drawer, Spin } from 'antd';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';

import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import { clearOTSettingsVersions } from '../state/settings.actions';
import {
  formatOTSettingsHistoryDate,
  formatOTSettingsHistoryEntries,
} from '../../helpers/overtime/overtimeSettingsHelpers';

const getVersionBadge = (version) => {
  const today = DateTime.local().startOf('day').toISODate();
  if (version.isActiveToday) {
    return { label: 'Active today', className: 'is-active' };
  }
  if (version.isLatest && version.effectiveDate > today) {
    return { label: 'Scheduled', className: 'is-pending' };
  }
  return { label: 'Past', className: 'is-past' };
};

export default function OvertimeSettingsHistoryDrawer({
  visible,
  onClose,
  otProfileId,
  loading,
}) {
  const dispatch = useDispatch();
  const versionsKey = otProfileId || 'company';
  const history = useSelector((state) => state.settings.otSettingsVersions?.[versionsKey]);
  const versions = history?.versions ?? [];

  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!visible || !versions.length) return;
    setSelectedVersionId((current) => current ?? versions[0]?.id ?? null);
  }, [visible, versions]);

  const handleClose = useCallback(() => {
    setSelectedVersionId(null);
    onClose();
  }, [onClose]);

  const handleClearHistory = useCallback(() => {
    CustomConfirmModal({
      title: 'Clear history?',
      content: 'This will permanently delete all version history for these overtime settings. This cannot be undone.',
      okText: 'Clear',
      cancelText: 'Cancel',
      onOk: async () => {
        setClearing(true);
        try {
          await dispatch(clearOTSettingsVersions(otProfileId));
          setSelectedVersionId(null);
        } finally {
          setClearing(false);
        }
      },
    });
  }, [dispatch, otProfileId]);

  const selectedVersion = versions.find((v) => v.id === selectedVersionId);
  const detailEntries = selectedVersion
    ? formatOTSettingsHistoryEntries(selectedVersion.settings)
    : [];

  const drawerTitle = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span>Overtime Settings History</span>
      {versions.length > 0 && (
        <OnTraccrButton
          title="Clear History"
          type="back"
          size="small"
          loading={clearing}
          onClick={handleClearHistory}
          style={{ marginRight: 32 }}
        />
      )}
    </div>
  );

  return (
    <Drawer
      title={drawerTitle}
      visible={visible}
      onClose={handleClose}
      width={440}
      className="overtime-settings-history-drawer"
    >
      {loading ? (
        <div className="overtime-settings-history-loading">
          <Spin />
        </div>
      ) : (
        <>
          {!versions.length ? (
            <div className="overtime-settings-history-empty">
              No version history yet.
            </div>
          ) : (
            <div className="overtime-settings-history-list">
              {versions.map((version) => {
                const isSelected = version.id === selectedVersionId;
                const badge = getVersionBadge(version);
                return (
                  <div
                    key={version.id}
                    role="button"
                    tabIndex={0}
                    className={`overtime-settings-history-version ${isSelected ? 'is-selected' : ''}`}
                    onClick={() => setSelectedVersionId(version.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setSelectedVersionId(version.id);
                      }
                    }}
                  >
                    <div className="overtime-settings-history-version-date">
                      {formatOTSettingsHistoryDate(version.effectiveDate)}
                    </div>
                    <div className={`overtime-settings-history-version-badge ${badge.className}`}>
                      {badge.label}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedVersion && (
            <div className="overtime-settings-history-detail">
              <h4 className="overtime-settings-history-detail-title">
                Effective
                {' '}
                {formatOTSettingsHistoryDate(selectedVersion.effectiveDate)}
              </h4>
              {detailEntries.map(({
                key, label, value, isEmpty,
              }) => (
                <div key={key} className="overtime-settings-history-detail-row">
                  <span className="overtime-settings-history-detail-label">{label}</span>
                  <span
                    className={`overtime-settings-history-detail-value ${isEmpty ? 'is-empty' : ''}`}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Drawer>
  );
}

OvertimeSettingsHistoryDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  otProfileId: PropTypes.string,
  loading: PropTypes.bool,
};

OvertimeSettingsHistoryDrawer.defaultProps = {
  otProfileId: null,
  loading: false,
};
